import * as React from 'react';
import classnames from 'classnames';
import { MenuItem } from 'lib/types';
import PersistentLink from 'core/components/PersistentLink';
import CareerBondvetLogo from 'assets/careerPageLogoWithText.inline.svg';
import LevelAccessMidnight from 'assets/level_access_icon_midnight.inline.svg';
import InputWithCta from 'core/components/InputWithCta/InputWithCta';
import { useIntl } from 'react-intl';
import * as styles from './Footer.module.scss';
import FooterLink from './FooterLink';
import Social from '../Social';
import NewsletterSignUpForCareers from '../NewsletterSignUpForCareers';
import CallToAction from '../CallToAction';
import AccessibeTrigger from '../AccessibeTrigger';
import ExternalLink from '../ExternalLink';

type CareersFooterProps = {
    className?: string;
    withTopMargin?: boolean;
};

function createLinks(
    prefix: string,
    items: ReadonlyArray<MenuItem>,
): React.ReactNode {
    return (
        <>
            {items.map((item: MenuItem, idx: number) => (
                <React.Fragment
                    key={[item.id, (item.sortOrder || idx).toString(10)].join(
                        '-',
                    )}
                >
                    <li>
                        <FooterLink item={item} prefix={prefix} />
                    </li>
                </React.Fragment>
            ))}
        </>
    );
}

export default function CareersFooter({
    className,
    withTopMargin = true,
}: CareersFooterProps) {
    const intl = useIntl();
    const prefix = '';

    const companyLinks: MenuItem[] = [
        {
            id: 'careers',
            to: '/careers/',
            label: 'Careers Home',
            boldLabel: '',
            sortOrder: 1,
            subItems: [],
            external: false,
        },
        {
            id: 'bond-vet-home',
            to: '/',
            label: 'Bond Vet Home',
            boldLabel: '',
            sortOrder: 2,
            subItems: [],
            external: false,
        },
    ];

    const navigationLinks: MenuItem[] = [
        {
            id: 'open-positions',
            to: '/careers/opportunities/open-positions/',
            label: 'Open Positions',
            boldLabel: '',
            sortOrder: 1,
            subItems: [],
            external: false,
        },
        {
            id: 'life-at-bond-vet',
            to: '/careers/life-at-bond-vet/',
            label: 'Life at Bond Vet',
            boldLabel: '',
            sortOrder: 2,
            subItems: [],
            external: false,
        },
        {
            id: 'students-graduates',
            to: '/careers/students-new-grads/',
            label: 'Students & Graduates',
            boldLabel: '',
            sortOrder: 3,
            subItems: [],
            external: false,
        },
        {
            id: 'opportunities',
            to: '/careers/opportunities/',
            label: 'Opportunities',
            boldLabel: '',
            sortOrder: 4,
            subItems: [],
            external: false,
        },
    ];

    const resourcesLinks: MenuItem[] = [
        {
            id: 'blog',
            to: '/careers/blog',
            label: 'Blog',
            boldLabel: '',
            sortOrder: 1,
            subItems: [],
            external: false,
        },
    ];

    const inputId = 'mail-signup';

    return (
        <footer
            className={classnames(styles.careerFooter, className, {
                [styles.noTopMargin]: !withTopMargin,
            })}
        >
            <div className={styles.container}>
                <section className={styles.section}>
                    <div>
                        <CareerBondvetLogo
                            className={styles.logo}
                            role="img"
                            aria-label="Bond Vet logo"
                        />
                        <p className={styles.subtitle}>
                            {intl.formatMessage({
                                id: 'careers.opportunities.footer.newsletterSignUpText',
                            })}
                        </p>

                        <NewsletterSignUpForCareers
                            inputId={inputId}
                            Component={InputWithCta}
                        />

                        <Social
                            exclude={['apple-podcasts', 'twitter', 'spotify']}
                            customUrls={{
                                instagram:
                                    'https://www.instagram.com/bondvetlife/',
                                facebook:
                                    'https://www.facebook.com/profile.php?id=61588447380362',
                                tiktok: 'https://www.tiktok.com/@bondvetlife',
                            }}
                        />
                    </div>
                </section>
                <div className={styles.linkSection}>
                    <section className={styles.section}>
                        <h3 className={styles.title}>
                            {intl.formatMessage({
                                id: 'careers.opportunities.footer.linksSection.title',
                            })}
                        </h3>
                        <ul className={styles.contactList}>
                            <li>
                                {intl.formatMessage(
                                    {
                                        id: [
                                            'careers',
                                            'opportunities',
                                            'footer',
                                            'linksSection',
                                            'contactList',
                                            'email',
                                        ].join('.'),
                                    },
                                    {
                                        value: () => (
                                            <a
                                                className={
                                                    styles.underlinedLink
                                                }
                                                href={`mailto:${intl.formatMessage(
                                                    {
                                                        id: 'app.recruitingEmail',
                                                    },
                                                )}`}
                                            >
                                                {intl.formatMessage({
                                                    id: 'app.recruitingEmail',
                                                })}
                                            </a>
                                        ),
                                    },
                                )}
                            </li>
                        </ul>
                    </section>

                    <section className={styles.section}>
                        <h3 className={styles.title}>
                            {intl.formatMessage({
                                id: 'careers.opportunities.footer.linksSection.sections.company',
                            })}
                        </h3>
                        <nav
                            aria-label={intl.formatMessage({
                                id: 'careers.opportunities.footer.linksSection.sections.company',
                            })}
                        >
                            <ul className={styles.footerList}>
                                {createLinks(prefix, companyLinks)}
                            </ul>
                        </nav>
                    </section>

                    <section className={styles.section}>
                        <h3 className={styles.title}>
                            {intl.formatMessage({
                                id: 'careers.opportunities.footer.linksSection.sections.navigation',
                            })}
                        </h3>
                        <nav
                            aria-label={intl.formatMessage({
                                id: 'careers.opportunities.footer.linksSection.sections.navigation',
                            })}
                        >
                            <ul className={styles.footerList}>
                                {createLinks(prefix, navigationLinks)}
                            </ul>
                        </nav>
                    </section>

                    <section className={styles.section}>
                        <h3 className={styles.title}>
                            {intl.formatMessage({
                                id: 'careers.opportunities.footer.linksSection.sections.resources',
                            })}
                        </h3>
                        <nav
                            aria-label={intl.formatMessage({
                                id: 'careers.opportunities.footer.linksSection.sections.resources',
                            })}
                        >
                            <ul className={styles.footerList}>
                                {createLinks(prefix, resourcesLinks)}
                            </ul>
                        </nav>
                    </section>
                </div>
            </div>

            <div className={styles.separator} />

            <div className={`${styles.container} ${styles.bottomContainer}`}>
                <section className={styles.bottomLinksSection}>
                    <ul className={styles.bottomList}>
                        <li>
                            <CallToAction
                                onClick={() => window.Cookiebot?.show()}
                            >
                                {intl.formatMessage({
                                    id: [
                                        'careers',
                                        'opportunities',
                                        'footer',
                                        'bottomLinksSection',
                                        'cookiePolicy',
                                    ].join('.'),
                                })}
                            </CallToAction>
                        </li>
                        <li>
                            <PersistentLink to="/privacy">
                                {intl.formatMessage({
                                    id: [
                                        'careers',
                                        'opportunities',
                                        'footer',
                                        'bottomLinksSection',
                                        'privacyPolicy',
                                    ].join('.'),
                                })}
                            </PersistentLink>
                        </li>
                        <AccessibeTrigger />
                        <li className={styles.iconContainer}>
                            <ExternalLink
                                href="https://bondvet.com/accessibility-statement"
                                aria-label="Level Access website accessibility icon."
                            >
                                <span className={styles.levelAccessIcon}>
                                    <LevelAccessMidnight />
                                </span>
                            </ExternalLink>
                        </li>
                    </ul>
                </section>

                <section className={classnames(styles.bottom)}>
                    {intl.formatMessage(
                        {
                            id: 'careers.opportunities.footer.copyrightText',
                        },
                        {
                            currentYear: new Date().getFullYear(),
                        },
                    )}
                </section>
            </div>
        </footer>
    );
}
